import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { DateRange } from 'react-day-picker';
import { BrowsersMetricsTable } from '@/components/metrics/devices/BrowsersMetricsTable';
import { PremiumDonutChart } from '@/components/metrics/charts/PremiumDonutChart';
import { Card } from '@/components/ui/card';
import { getBrowserIconComponent } from '@/components/metrics/browsers/BrowserIcons';

interface BrowsersTabContentProps {
  date?: DateRange | undefined;
  videoId: string | null;
  devicesData?: any;
  metricsEnhanced?: any;
}

interface BrowserData {
  name: 'Chrome' | 'Safari' | 'Firefox' | 'Edge' | 'Opera' | 'Other' | string;
  views: number;
  percentage: number;
}

// Traduz nomes de navegadores para português
const translateBrowserName = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower === 'other' || lower === 'others') return 'Outros';
  if (lower === 'navegador desconhecido' || lower === 'unknown') return 'Desconhecido';
  return name;
};

// Mock data for browsers (4 items)
const browsersMockData: BrowserData[] = [
  { name: 'Chrome', views: 18392, percentage: 72.3 },
  { name: 'Safari', views: 5247, percentage: 20.6 },
  { name: 'Edge', views: 1843, percentage: 7.2 },
  { name: 'Firefox', views: 832, percentage: 3.3 }
];

// Premium Browser Card Component
function PremiumBrowserCard({ browser }: { browser: BrowserData }) {
  useLanguage();
  return (
    <div 
      className="bg-white border border-[#E5E7EB] rounded-[10px] px-[14px] py-[8px] min-h-[42px]
                 flex items-center justify-between
                 hover:bg-[#F9FAFB] transition-all duration-150 ease-out cursor-default"
    >
      {/* Lado Esquerdo */}
      <div className="flex items-center gap-[10px]">
        {getBrowserIconComponent(browser.name, 18)}
        <div>
        <p className="text-[14px] font-medium text-[#0F172A]">
            {translateBrowserName(browser.name)}
          </p>
          <p className="text-[13px] font-normal text-[#6B7280]">
            {browser.percentage}{translateInterfaceText("% das visualizações")}</p>
        </div>
      </div>
      
      {/* Lado Direito */}
      <div className="text-right">
        <p className="text-[15px] font-medium text-[#1E3A8A]">
          {browser.views.toLocaleString(getInterfaceLanguage())}
        </p>
        <p className="text-[11px] text-[#6B7280]">{translateInterfaceText("visualizações")}</p>
      </div>
    </div>
  );
}

// Premium Browser Cards List
function PremiumBrowserCards({ data }: { data: BrowserData[] }) {
  useLanguage();
  return (
    <div className="space-y-[8px]">
      {data.map((browser, index) => (
        <PremiumBrowserCard key={index} browser={browser} />
      ))}
    </div>
  );
}

export function BrowsersTabContent({
  date,
  videoId,
  devicesData,
  metricsEnhanced
}: BrowsersTabContentProps) {
  useLanguage();
  const browsersData = metricsEnhanced?.browsers || [];

  // Mapear navegadores da API para o formato esperado
  const browsers: BrowserData[] = browsersData
    .map(browser => ({
      name: browser.browser || 'Other',
      views: browser.views,
      percentage: parseFloat(browser.percentual)
    }))
    .sort((a, b) => b.views - a.views) // Ordenar do maior para o menor
    .slice(0, 4); // Limitar a 4 items

  return (
    <div className="space-y-3">
      {/* Top Section: Donut + Cards */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Gráfico Donut Premium */}
        <PremiumDonutChart
          title={translateInterfaceText("Distribuição por Navegadores")}
          data={browsers.map((browser: BrowserData) => ({
            name: translateBrowserName(browser.name),
            value: browser.views,
            percentage: browser.percentage
          }))}
          renderLegends={false}
          showBrowserIcons={false}
        />
        
        {/* Premium Browser Cards */}
        <Card className="border border-[#E2E8FF] rounded-[14px] bg-white p-4">
          <h3 className="text-[15px] font-semibold text-[#1A1A1A] mb-3">
            {translateInterfaceText("Detalhes por Navegador")}</h3>
          <PremiumBrowserCards data={browsers} />
        </Card>
      </div>
      
      
      {/* Bottom Section: Table */}
      <div>
        <BrowsersMetricsTable data={browsersData} pitches={metricsEnhanced?.videoInfo?.pitches ?? []} />
      </div>
    </div>
  );
}
